import React, { useState } from 'react';
import { Leaf, Search, Filter } from 'lucide-react';

const FreeFoodsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Mock data - replace with actual API call
  const freeFoods = [
    {
      id: '1',
      name: 'Lettuce',
      brand: 'Fresh Farms',
      protein_per_100g: 0.2,
      description: 'Fresh green lettuce',
      photo_urls: [],
    },
    {
      id: '2',
      name: 'Cucumber',
      brand: 'Organic Valley',
      protein_per_100g: 0.3,
      description: 'Organic cucumber',
      photo_urls: [],
    },
    {
      id: '3',
      name: 'Bell Pepper',
      brand: 'Fresh Farms',
      protein_per_100g: 0.4,
      description: 'Red bell pepper',
      photo_urls: [],
    },
  ];

  const filteredFoods = freeFoods.filter(food =>
    food.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    food.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedFoods = [...filteredFoods].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'brand':
        return a.brand.localeCompare(b.brand);
      case 'protein':
        return a.protein_per_100g - b.protein_per_100g;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-responsive py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Leaf className="w-6 h-6 text-success-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Free Foods
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            These foods contain less than 0.5g of protein per 100g and are generally safe for PKU diets. 
            Always consult with your healthcare provider for personalized dietary advice.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search free foods..."
                  className="form-input pl-10"
                />
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-input"
                >
                  <option value="name">Sort by Name</option>
                  <option value="brand">Sort by Brand</option>
                  <option value="protein">Sort by Protein</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">
            Showing {filteredFoods.length} free food{filteredFoods.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Food Grid */}
        {sortedFoods.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedFoods.map((food) => (
              <div key={food.id} className="card hover:shadow-medium transition-shadow">
                <div className="card-body">
                  {/* Food Image Placeholder */}
                  <div className="w-full h-48 bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                    {food.photo_urls.length > 0 ? (
                      <img
                        src={food.photo_urls[0]}
                        alt={food.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Leaf className="w-12 h-12 text-gray-300" />
                    )}
                  </div>

                  {/* Food Info */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {food.name}
                      </h3>
                      {food.brand && (
                        <p className="text-sm text-gray-600">{food.brand}</p>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="badge badge-success">
                        {food.protein_per_100g}g protein/100g
                      </span>
                      <span className="text-sm text-gray-500">
                        Free Food
                      </span>
                    </div>

                    {food.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {food.description}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-2 pt-2">
                      <button className="btn btn-outline btn-sm flex-1">
                        View Details
                      </button>
                      <button className="btn btn-primary btn-sm flex-1">
                        Add to List
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* No Results */
          <div className="card">
            <div className="card-body text-center py-12">
              <Leaf className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No free foods found
              </h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? `No foods match "${searchQuery}". Try a different search term.`
                  : 'There are no free foods available at the moment.'
                }
              </p>
            </div>
          </div>
        )}

        {/* Information Box */}
        <div className="mt-8">
          <div className="card bg-green-50 border-green-200">
            <div className="card-body">
                              <h3 className="text-lg font-semibold text-green-900 mb-2">
                About Free Foods
              </h3>
              <p className="text-green-800 mb-3">
                Free foods are those that contain less than 0.5g of protein per 100g. 
                These foods can typically be consumed without counting towards your daily protein exchange limit.
              </p>
              <ul className="text-green-800 text-sm space-y-1">
                <li>• Always verify nutrition information on packaging</li>
                <li>• Consult with your healthcare provider for personalized advice</li>
                <li>• Portion sizes still matter for overall nutrition</li>
                <li>• Some foods may vary in protein content between brands</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeFoodsPage;
